import { useState } from 'react';

import { SCROLLBAR_CONTAINER } from '@/components/atom/ChatAssetTree/constants';
import { useAppDispatch } from '@/configuration/setup/hooks';
import { useLazyFetchThreadsQuery } from '@/services/threadApi/threadApi';
import { mapFetchThreadsRequestParams } from '@/services/threadApi/utils/mapFetchThreadsRequestParams';
import { setScrollPosition } from '@/store/sidebar/sidebarSlice';

import { TUseThreadPaginationParams } from '../types';

/**
 * Custom hook for infinite scroll pagination, it looads more threads when user scrolls to bottom
 * 
 * @param userId - Current user identifier
 * @param searchValue - Current search query
 * @param sortOrder - Current sort order
 * @param favoriteOnly - Filter to favorites only
 * @param hasNextPage - Whether more pages exist
 * @param currentPage - Current page number
 * @param activeSidebarItem - Currently active sidebar item
 * @returns {Promise<void>} loadMoreThreads function
 */

export const useThreadPagination = ({
    userId,
    searchValue,
    sortOrder,
    favoriteOnly,
    hasNextPage,
    currentPage,
    
}: TUseThreadPaginationParams) => {
  const dispatch = useAppDispatch();
  const [fetchThreads] = useLazyFetchThreadsQuery();
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  // INFO: Load next page of threads and prevents duplicate requests

  const loadMoreThreads = async () => {
    if (isFetchingMore || !hasNextPage) return;
    
    setIsFetchingMore(true);

    //INFO: Mantains scroll position before fetch to later restore when new nodes are added to the DOM or the sidebar is reopened
    dispatch(setScrollPosition(document.querySelector(SCROLLBAR_CONTAINER)?.scrollTop || 0));

    try {
      await fetchThreads(
        mapFetchThreadsRequestParams({
          userId,
          sortOrder,
          search: searchValue,
          page: currentPage + 1,
          onlyFavorites: favoriteOnly,
        })
      );
    } finally {
      setIsFetchingMore(false);
    }
  };

  return { loadMoreThreads };
}